import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useFileDownloadMutation } from "../../../../../../../../api/services/client/clientApis";
import { useGetProgramDocumentDetailsById } from "../../../../../../../../api/services/program/programApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiViewMorePopOver,
} from "../../../../../../../../components";
import FileUploadPreview from "../../../../../../../../components/fileUpload/FileUploadPreview";
import {
  PAGE_IDS,
  viewMorePopOverGridSize,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getFullName,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { programUploadNewDocumentSitecoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ViewDocumentModalWrapper } from "./ViewDocumentModal.style";

interface ViewDocumentModalProps {
  documentId: string;
  blobId: string;
  open: boolean;
  onClose: () => void;
}

const pageId = PAGE_IDS.PROGRAM_VIEW_DOCUMENT;
export const ViewDocumentModal: FC<ViewDocumentModalProps> = ({
  documentId,
  blobId,
  open,
  onClose,
}) => {
  const { useFetchLabel, commonLabels, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeMedium: gridSize } = useGridContainer(true);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const [newFile, setNewFile] = useState<File | null>(null);

  const {
    mutateAsync: getProgramDocumentDetailsById,
    data,
    isPending: isGetProgramDocumentDetailsPending,
    isError: isGetProgramDocumentDetailsError,
  } = useGetProgramDocumentDetailsById();

  const {
    mutate: downloadFile,
    isPending: isPendingOnDownload,
    isSuccess: isSuccessOnDownload,
    isError: isErrorOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  useEffect(() => {
    if (!documentId || !blobId) return;
    downloadFile({
      documentId: blobId,
      documentType: "program",
    });
    getProgramDocumentDetailsById(documentId);
  }, [documentId, blobId]);

  useEffect(() => {
    if (isErrorOnDownload || isGetProgramDocumentDetailsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isErrorOnDownload, isGetProgramDocumentDetailsError]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isViewDocumentLoading: {
          isVisible: isPendingOnDownload || isGetProgramDocumentDetailsPending,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isViewDocumentLoading: { isVisible: false } }));
    };
  }, [isPendingOnDownload, isGetProgramDocumentDetailsPending]);

  useEffect(() => {
    if (dataOnDownload && data) {
      const file = new File([dataOnDownload.blob], data.fileName ?? "", {
        type: dataOnDownload.blob.type,
      });
      setNewFile(file);
    }
  }, [isSuccessOnDownload, dataOnDownload, data]);

  const {
    uploadNewDocumentTitle,
    programDocumentTypeList,
    attachmentSection,
    description,
    documentDetailsSection,
    type,
    uploadDate: uploadDateLabel,
    uploadedBy: uploadedByLabel,
    viewDocumentNamePopupHeader,
  } = programUploadNewDocumentSitecoreKeys;
  const getLabel = useFetchLabel(uploadNewDocumentTitle);

  const getFormattedDate = (dateString: string) => {
    if (isEmpty(dateString)) return "";
    return convertToUserFormat(dateString, userCountryDateFormat);
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={replaceHashWithValue(
        getLabel(viewDocumentNamePopupHeader),
        getValueOrBlank(data?.fileName),
      )}
      hasLargeContent
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={onClose}
      stackButtonsOnMobile={true}
      className="view-document-modal"
    >
      <ViewDocumentModalWrapper>
        <AccordionHeader
          title={renderHeader(getLabel(documentDetailsSection))}
          showExpandCollapseIcon={false}
        >
          {data && (
            <MuiBox className="content">
              <MuiGrid container spacing={2} className="document-type">
                <MuiGrid size={gridSize}>
                  <MuiDoubleLabel
                    id={`${pageId}-${type}`}
                    label={getLabel(type)}
                    value={fetchValueByKeyOfDropdown(
                      uploadNewDocumentTitle,
                      programDocumentTypeList,
                      getValueOrBlank(data?.type),
                    )}
                  />
                </MuiGrid>

                <MuiGrid size={gridSize}>
                  <MuiDoubleLabel
                    id={`${pageId}-${uploadDateLabel}`}
                    label={getLabel(uploadDateLabel)}
                    replaceEmptyWithDash
                    value={getFormattedDate(data?.uploadedDate)}
                  />
                </MuiGrid>

                <MuiGrid size={gridSize}>
                  <MuiDoubleLabel
                    id={`${pageId}-${uploadedByLabel}`}
                    label={getLabel(uploadedByLabel)}
                    replaceEmptyWithDash
                    value={getFullName(
                      data.createdBy?.firstName,
                      data.createdBy?.lastName,
                      true,
                    )}
                  />
                </MuiGrid>

                <MuiGrid size={viewMorePopOverGridSize}>
                  <MuiDoubleLabel
                    id={`${pageId}-${description}`}
                    label={getLabel(description)}
                    replaceEmptyWithDash
                    value={
                      isNotEmpty(data?.description) ? (
                        <MuiViewMorePopOver
                          anchorText={commonLabels.viewMore}
                          message={getValueOrBlank(data?.description)}
                          placement="bottom-end"
                        />
                      ) : (
                        ""
                      )
                    }
                  />
                </MuiGrid>
              </MuiGrid>
            </MuiBox>
          )}
        </AccordionHeader>
        <AccordionHeader
          title={renderHeader(getLabel(attachmentSection))}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="content file-upload">
            {newFile && (
              <FileUploadPreview showCloseIcon={false} file={newFile} />
            )}
          </MuiBox>
        </AccordionHeader>
        <MuiBox />
      </ViewDocumentModalWrapper>
    </MuiModal>
  );
};
